@if (scan) {
<nz-modal
  [nzWidth]="nzWidth"
  [nzStyle]="nzStyle"
  [nzBodyStyle]="{ height: 'calc(75vh - 108px)', display: 'flex', 'flex-direction': 'column', overflow: 'hidden' }"
  nzMaskClosable="false" [nzVisible]="!!scan"
  [nzDraggable]="true"
  [nzMaskStyle]="isGuestOnly ? {} : { 'pointer-events': 'none' }"
  [nzWrapClassName]="isGuestOnly ? '' : 'nonblocking-dialog'"
  (nzAfterOpen)="onAfterOpen()"
  (nzAfterClose)="onAfterClose()"
  (nzOnCancel)="cancel()"
  [nzTitle]="title ?? modalTitle">

  <ng-template #modalTitle>
    <span i18n>Scan: {{scan.sequenceInBatch | number:'1.0-0'}} </span><br>
    <span i18n>Scanned at:   {{(scan.scannedTime) | date:'yyyy-MM-dd HH:mm:ss'}}</span><br>
    <span i18n>Inspected at: {{scan.inspectedTime | date:'yyyy-MM-dd HH:mm:ss'}}</span><br>
    <span i18n> dE: {{scan.dE | number:'1.1-1'}} &nbsp; dC: {{euclideanDeltaC(scan.productionBatch?.color?.lab, scan.predicted) | number:'1.1-1'}} &nbsp; CI: {{scan.ci | number:'1.1-1'}}</span>
  </ng-template>

  <ng-container *nzModalContent>

    @if (authState.hasAnyRole(UserRole.Admin)) {
    <div style="display: flex; justify-content: flex-end; padding-bottom: 8px;">
      <button nz-button nzDanger nzType="default"
              nz-popconfirm
              i18n-nzPopconfirmTitle nzPopconfirmTitle="Delete this scan? This permanently removes the image files from disk and the images_links row."
              (nzOnConfirm)="deleteScan()"
              nzPopconfirmPlacement="bottomRight">
        <span nz-icon nzType="delete"></span>
        <span i18n>Delete</span>
      </button>
    </div>
    }

    <nz-tabs [nzAnimated]="false" [(nzSelectedIndex)]="selectedTabIndex"
      (nzSelectedIndexChange)="selectedTabIndexChange.emit($event)">
      <nz-tab i18n-nzTitle nzTitle="Scan">
        @if (scan.reasons?.length) {
        <app-failure-reason [reasons]="scan.reasons"></app-failure-reason>
        }
        <div class="scan-image-container">
          <img style="width: 100%" nz-image i18n-alt alt="image" [nzFallback]="fallbackImage"
            [nzSrc]="scan.imageUrl ?? fallbackImage" (load)="onScanImageLoad($event)" />
          <app-inspection-overlay [overlay]="scan.inspectionOverlay" [imageSize]="overlayImageSize" />
        </div>
        <nz-table nzTemplateMode>
          <thead>
            <tr style="text-align: center;">
              <th i18n style="text-align: center;">Source</th>
              <th style="text-align: center;">L</th>
              <th style="text-align: center;">A</th>
              <th style="text-align: center;">B</th>
              <th style="text-align: center;">Bucket<br>L</th>
              <th style="text-align: center;">Bucket<br>Splash</th>
            </tr>
          </thead>
          <tbody>
            @if (scan.median; as lab) {
            <tr>
              <td i18n>Camera</td>
              <td>{{ lab.l | number : '1.2-2' }}</td>
              <td>{{ lab.a | number : '1.2-2' }}</td>
              <td>{{ lab.b | number : '1.2-2' }}</td>
              <td>{{ scan.bucketL | number : '1.2-2' }}</td>
              <td>{{ scan.bucketSplash | percent }}</td>
            </tr>
            }
            @if (scan.productionBatch?.color?.lab; as lab) {
            <tr [style.background]="colorPicker(lab)">
              <td i18n>Reference</td>
              <td>{{ lab.l | number : '1.2-2' }}</td>
              <td>{{ lab.a | number : '1.2-2' }}</td>
              <td>{{ lab.b | number : '1.2-2' }}</td>
            </tr>
            }
            @if (scan.predicted; as lab) {
            <tr [style.background]="colorPicker(lab)">
              <td i18n>Detected</td>
              <td>{{ lab.l | number : '1.2-2' }}</td>
              <td>{{ lab.a | number : '1.2-2' }}</td>
              <td>{{ lab.b | number : '1.2-2' }}</td>
            </tr>
            }
            @if (scan.predicted && scan.productionBatch?.color?.lab) {
            <tr>
              <td i18n style="text-align: center;">Δ</td>
              <td>{{ scan.predicted.l - scan.productionBatch!.color!.lab!.l | number : '1.2-2' }}</td>
              <td>{{ scan.predicted.a - scan.productionBatch!.color!.lab!.a | number : '1.2-2' }}</td>
              <td>{{ scan.predicted.b - scan.productionBatch!.color!.lab!.b | number : '1.2-2' }}</td>
            </tr>
            }
          </tbody>
        </nz-table>
        @if (scan.dE) {
        <div>
          dE: {{ scan.dE | number : '1.2-2' }}
        </div>
        }
        @if (euclideanDeltaC(scan.productionBatch?.color?.lab, scan.predicted); as dc) {
        <div>
          dC: {{ dc | number : '1.2-2' }}
        </div>
        }
        @if (scan.ci != undefined) {
        <div>
          CI: {{ scan.ci | number : '1.2-2' }}
        </div>
        }
      </nz-tab>
      <nz-tab i18n-nzTitle nzTitle="Details">
        <div style="display: flex; flex-direction: column; gap: 15px; padding-top: 10px;">
          <div>
            <h4 i18n>Model Version</h4>
            @if (inspectionModel) {
              <span [nzTooltipTitle]="inspectionModel.description || noDescriptionText" nz-tooltip>
                {{ inspectionModel.version }}
              </span>
            } @else {
              <span i18n style="color: gray;">&lt;Inspection Model Not Set&gt;</span>
            }
          </div>
          <div>
            <h4 i18n>Comment</h4>
            <textarea nz-input [(ngModel)]="scan.comment" [nzAutosize]="{ minRows: 4, maxRows: 10 }"
              i18n-placeholder placeholder="Markdown comment..."></textarea>
          </div>
          <div>
            <h4 i18n="@@spectroTitle">Measured by Spectrophotometer — absolute L / a / b as the device shows them (not differences)</h4>
            <div class="spectro-grid">
              <span i18n="@@spectroExpectedRow">Expected</span>
              @for (key of spectroKeys; track key) {
              <span>{{ (spectroExpected?.[key] | number : '1.2-2') ?? '---' }}</span>
              }
              <span i18n="@@spectroMeasuredRow">Measured</span>
              <nz-input-wrapper nzAddonBefore="L">
                <input #spectroL nz-input type="number" step="0.1"
                  [style.background-color]="scan.measuredBySpectro?.l == null ? '#FFFFE0' : ''"
                  [(ngModel)]="scan.measuredBySpectro!.l"
                  (blur)="spectroLLeft = true"
                  [disabled]="!canEditTrainingData" />
              </nz-input-wrapper>
              <nz-input-wrapper nzAddonBefore="a">
                <input nz-input type="number" step="0.1"
                  [style.background-color]="scan.measuredBySpectro?.a == null ? '#FFFFE0' : ''"
                  [(ngModel)]="scan.measuredBySpectro!.a"
                  [disabled]="!canEditTrainingData" />
              </nz-input-wrapper>
              <nz-input-wrapper nzAddonBefore="b">
                <input nz-input type="number" step="0.1"
                  [style.background-color]="scan.measuredBySpectro?.b == null ? '#FFFFE0' : ''"
                  [(ngModel)]="scan.measuredBySpectro!.b"
                  [disabled]="!canEditTrainingData" />
              </nz-input-wrapper>
              <span>Δ</span>
              @for (key of spectroKeys; track key) {
              <span>{{ (spectroDelta(key) | number : '1.2-2') ?? '---' }}</span>
              }
            </div>
            @if (showSpectroError) {
              <div class="spectro-error" role="alert" i18n="@@spectroReadingIncomplete">Enter all three values L, a and b, or leave all three empty.</div>
            }
            @if (spectroLooksLikeDelta(true)) {
            <div role="alert" style="color: #ad6800; margin-top: 4px;">
              <div>
                <span nz-icon nzType="warning" nzTheme="fill" style="margin-inline-end: 4px"></span>
                <span i18n="@@spectroLooksLikeDelta">L = {{ scan.measuredBySpectro!.l }} is {{ spectroDeltaMaxL }} or less: it looks like a difference, not an absolute reading.</span>
              </div>
              <div i18n="@@spectroEnterAbsolute">Enter the absolute value the spectrophotometer gave, not a difference.</div>
            </div>
            }
          </div>
          <div>
            <h4 i18n>Quality for Training</h4>
            <nz-select [(ngModel)]="scan.qualityForTraining" nzAllowClear i18n-nzPlaceHolder nzPlaceHolder="Select quality For Training"
              style="width: 100%;" [nzDisabled]="!canEditTrainingData">
              @for (q of trainingState.qualityForTrainingConstants(); track q) {
              <nz-option [nzValue]="q" [nzLabel]="q"></nz-option>
              }
            </nz-select>
          </div>
          @if (canEditReason) {
          <!-- AC-4: Reason dropdown — visible to Admin & Laboratory only (Operator uses the popup). -->
          <div>
            <h4 i18n>Status-change reason</h4>
            <nz-select [(ngModel)]="scan.operatorStatusChangeReason" nzAllowClear
              i18n-nzPlaceHolder nzPlaceHolder="Select reason (optional)"
              style="width: 100%;">
              @for (r of reasonOptions; track r.key) {
                <nz-option [nzValue]="r.key" [nzLabel]="r.label"></nz-option>
              }
            </nz-select>
          </div>
          }
          @if (authState.hasAnyRole(UserRole.Admin)) {
          <!-- DEV-286: scan tags (Admin only). Nothing is loaded until "Show tags" is clicked. -->
          <div>
            <h4 i18n>Tags</h4>
            <app-scan-tags-list [scanId]="scan.id"></app-scan-tags-list>
          </div>
          }
          <button nz-button nzType="primary" (click)="save()"><span i18n>Save</span></button>
        </div>
      </nz-tab>
    </nz-tabs>
    <div class="nav-button-box">
      <button nz-button nzType="default" (click)="backward()">
        <span class="nav-arrow" [style]="mirrorArrow()" nz-icon nzType="left"></span>
        <ng-container i18n>back</ng-container>
      </button>
      <button nz-button nzType="default" (click)="forward()">
        <ng-container i18n>forward</ng-container>
        <span class="nav-arrow" [style]="mirrorArrow()" nz-icon nzType="right"></span>
      </button>
    </div>
  </ng-container>
  <div *nzModalFooter nzGrid>
    <nz-spin [nzSpinning]="authState.snapshot.busy">
      <div nz-row nzAlign="middle" nzJustify="space-between">
        <div nz-col [nzSpan]="14" style="display: flex; flex-direction: column; align-items: flex-start; gap: 4px;">
          <!-- AC-2: ngModelChange is intercepted — Operator opens reason popup; non-Operator emits directly. -->
          <nz-radio-group style="text-align: start" nzButtonStyle="outline" [ngModel]="scan.state"
            (ngModelChange)="onRadioStatusChange($event)">
            <label nz-radio-button nzValue="Passed">
              <span nz-icon nzType="check-circle" nzTheme="fill" style="color : green; margin-inline-end: 4px"></span>
              <span i18n>Passed</span>
            </label>
            <label nz-radio-button nzValue="Failed" i18n-nzTooltipTitle nz-tooltip
              nzTooltipTitle="use this if you going to throw a bucket away and create anew manual PAKA">
              <span nz-icon nzType="close-circle" nzTheme="fill" style="color : red; margin-inline-end: 4px"></span>
              <span i18n>Failed</span>
            </label>
            <label nz-radio-button nzValue="Ignored" i18n-nzTooltipTitle nz-tooltip
              nzTooltipTitle="use this if you will send the same bucket again through the scanner. Example if it was not mixed well and no new Manual Job will be created">
              <span nz-icon nzType="question-circle" nzTheme="fill" style="color : grey; margin-inline-end: 4px"></span>
              <span i18n>Ignore</span>
            </label>
          </nz-radio-group>
        
        </div>        
        @if (extraActionTitle) {
        <button nz-col [nzSpan]="4" nz-button nzType="default" (click)="extraAction()">{{ extraActionTitle }}
        </button>
        }
        <button nz-col [nzSpan]="4" nz-button nzType="primary" (click)="defaultAction()">{{ defaultActionTitle }}
        </button>
      </div>
        @if (scan.scanStateSetByRole) {
        <div style="text-align: start; display: flex; align-items: center; gap: 6px; flex-wrap: wrap;">
          <span i18n>Status set by:</span>
          <nz-tag [nzColor]="scan.scanStateSetByRole === 'Algo' ? 'default' : 'blue'" i18n-nzTooltipTitle
            nzTooltipTitle="State set by" nz-tooltip>
            {{ scan.scanStateSetByRole }}
          </nz-tag>
          <!-- AC-5: render localized reason label when present; null → render nothing -->
          @if (currentReasonLabel) {
            <nz-tag nzColor="purple">{{ currentReasonLabel }}</nz-tag>
          }
        </div>
        }
    </nz-spin>
  </div>
</nz-modal>
}

<!-- AC-1/AC-2: Operator reason-picker popup — mounted outside nz-modal to avoid z-index stacking issues -->
<app-operator-reason-dialog
  (reasonSelected)="onReasonSelected($event)"
  (cancelled)="onReasonCancelled()"
></app-operator-reason-dialog>
